import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Award, AlertCircle, User as UserIcon, Sparkles, Brain, RefreshCw, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import YearlyProgress from '@/components/YearlyProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentDetail() {
  const { studentId, subdomain } = useParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  const basePath = subdomain ? `/${subdomain}/admin` : '/admin';

  useEffect(() => {
    fetchRecords();
    fetchAiAnalysis();
  }, [studentId]);

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/admin/students/${studentId}/records`, {
        headers: getAuthHeaders(),
      });
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAnalysis = async () => {
    setLoadingAi(true);
    try {
      const response = await axios.get(`${API}/admin/ai-analysis/student/${studentId}`, {
        headers: getAuthHeaders(),
      });
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setAiAnalysis(null);
    } finally {
      setLoadingAi(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Жүктелуде...</div>;
  }

  const totalScore = records.reduce((sum, record) => {
    return sum + (record.behavior_type === 'merit' ? record.points : -record.points);
  }, 0);

  const meritCount = records.filter(r => r.behavior_type === 'merit').length;
  const demeritCount = records.filter(r => r.behavior_type === 'demerit').length;

  return (
    <div className="space-y-6 fade-in" data-testid="student-detail">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-3xl font-bold heading-font tracking-tight">
            {records[0]?.student_name || 'Оқушы'}
          </h2>
          <p className="text-muted">Толық мәліметтер</p>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-bold heading-font score-animation ${
            totalScore >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {totalScore >= 0 ? '+' : ''}{totalScore}
          </div>
          <p className="text-sm text-muted">Жалпы ұпай</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <Award className="w-6 h-6 text-green-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-green-600">{meritCount}</div>
            <p className="text-xs text-muted">Мақтау</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-red-600">{demeritCount}</div>
            <p className="text-xs text-muted">Сөгіс</p>
          </CardContent>
        </Card>
        <Card className="paper-card">
          <CardContent className="p-4 text-center">
            <UserIcon className="w-6 h-6 text-primary mx-auto mb-1" />
            <div className="text-2xl font-bold">{records.length}</div>
            <p className="text-xs text-muted">Барлық жазба</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis Card */}
      <Card className="paper-card border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-purple-700">
            <Sparkles className="w-5 h-5" />
            AI Тәрбиелік талдау
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingAi ? (
            <div className="flex items-center justify-center py-6">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mr-3" />
              <span className="text-purple-600">AI талдау жасалуда...</span>
            </div>
          ) : aiAnalysis ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                <p className="text-gray-700 leading-relaxed">{aiAnalysis}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchAiAnalysis}
                className="text-purple-600 hover:text-purple-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Қайта талдау
              </Button>
            </div>
          ) : (
            <p className="text-muted text-center py-4">
              AI талдау қолжетімсіз
            </p>
          )}
        </CardContent>
      </Card>

      {/* Yearly Progress & History */}
      <Card className="paper-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Жылдық прогрес және тарих
          </CardTitle>
        </CardHeader>
        <CardContent>
          <YearlyProgress 
            studentId={studentId}
            studentName={records[0]?.student_name}
            getAuthHeaders={getAuthHeaders}
            isOwnProfile={false}
          />
        </CardContent>
      </Card>

      {/* Records List */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Барлық жазбалар</h3>
        {records.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted">Жазбалар табылмады</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {records.map((record) => (
              <Card key={record.id} className="paper-card" data-testid={`record-${record.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {record.behavior_type === 'merit' ? (
                          <Award className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <h3 className="font-semibold text-lg">{record.behavior_name}</h3>
                        <span className={record.behavior_type === 'merit' ? 'merit-badge' : 'demerit-badge'}>
                          {record.behavior_type === 'merit' ? '+' : '-'}{record.points} ұпай
                        </span>
                      </div>
                      <p className="text-sm text-muted mb-1">
                        Мұғалім: {record.teacher_name}
                      </p>
                      {record.comment && (
                        <p className="text-sm bg-secondary/30 p-3 rounded-lg mt-2">
                          {record.comment}
                        </p>
                      )}
                      <p className="text-xs text-secondary mt-2">
                        {format(new Date(record.created_at), 'dd MMM yyyy, HH:mm')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
